import { getCookie } from '@/utils/auth-utils'

const STORAGE_KEY = 'aol.print-filters.v1'
const VIEWS = ['batches', 'samples']
const FILTER_KEYS = [
  'startDate', 'endDate', 'dateType', 'packagingType', 'integrated', 'printed',
  'collectionPointName', 'collectionPointId', 'source', 'sampleStatus', 'patientName',
  'codeOriginLis', 'codeOriginAlvaro', 'q', 'batchLabel', 'idFile', 'processingOrigin',
  'idRecollect', 'textField', 'showMoreFilters', 'tab', 'listFilter',
]
const SHARED_KEYS = ['startDate', 'endDate', 'packagingType', 'integrated', 'source']
const SEARCH_KEYS = ['patientName', 'codeOriginLis', 'codeOriginAlvaro', 'q', 'batchLabel']
let memory = null

function scope() {
  const profile = getCookie('profile') || {}
  return JSON.stringify([profile.id_operator || profile.id || profile.username, profile.id_laboratory])
}

function emptySession() {
  return { scope: scope(), shared: {}, queries: {}, lastView: 'batches' }
}

function readSession() {
  let session = memory
  try {
    session = JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || session
  } catch (_) {
    // Keep navigation working when session storage is unavailable.
  }
  if (!session || session.scope !== scope() || !session.queries || !session.shared) {
    session = emptySession()
  }
  memory = session
  return session
}

function writeSession(session) {
  memory = session
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch (_) {
    // The in-memory snapshot still lasts for this application session.
  }
}

function cleanQuery(query = {}) {
  return FILTER_KEYS.reduce((result, key) => {
    const value = query[key]
    if (value === undefined) return result
    if (Array.isArray(value)) {
      result[key] = value.length ? value.map(item => String(item)) : ''
    } else if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
      result[key] = value === null ? '' : String(value)
    }
    return result
  }, {})
}

export function restorePrintFilters(view, query = {}) {
  const session = readSession()
  const restored = { ...session.queries[view], ...session.shared }
  // Links from orders and recollection start a new search. Keep the selected
  // period and quick filters, but do not combine that search with old text.
  if (query.idRecollect || SEARCH_KEYS.some(key => query[key])) {
    [...SEARCH_KEYS, 'textField', 'idRecollect', 'processingOrigin', 'listFilter'].forEach(key => {
      delete restored[key]
    })
  }
  return cleanQuery({ ...restored, ...query })
}

export function savePrintFilters(view, filters) {
  if (!VIEWS.includes(view)) return
  const session = readSession()
  const query = cleanQuery(filters)
  session.queries[view] = query
  SHARED_KEYS.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(query, key)) {
      session.shared[key] = Array.isArray(query[key]) ? query[key].slice() : query[key]
    }
  })
  session.lastView = view
  writeSession(session)
}

function sameQuery(left, right) {
  const canonical = query => JSON.stringify(Object.keys(query).sort().map(key => [key, query[key]]))
  return canonical(left) === canonical(right)
}

export function arePrintFiltersEqual(left, right) {
  return sameQuery(cleanQuery(left), cleanQuery(right))
}

export async function persistPrintFilters(vm, view, filters) {
  const query = cleanQuery({ tab: vm.$route.query.tab, ...filters })
  savePrintFilters(view, query)
  if (sameQuery(vm.$route.query, query)) return
  try {
    await vm.$router.replace({ path: vm.$route.path, query })
  } catch (error) {
    if (error.name !== 'NavigationDuplicated') throw error
  }
}

export function getPrintListLocation(view, currentQuery = {}) {
  const shared = {}
  SHARED_KEYS.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(currentQuery, key)) shared[key] = currentQuery[key]
  })
  return { path: `/print-tag/${view}`, query: restorePrintFilters(view, shared) }
}

export function getPrintDetailQuery(route, extra = {}) {
  const view = /\/samples\/?$/.test(route.path) ? 'samples' : 'batches'
  return { ...restorePrintFilters(view, route.query), returnView: view, ...extra }
}

export function getPrintReturnLocation(query = {}) {
  const session = readSession()
  const view = VIEWS.includes(query.returnView)
    ? query.returnView
    : VIEWS.includes(session.lastView) ? session.lastView : 'batches'
  return { path: `/print-tag/${view}`, query: restorePrintFilters(view, cleanQuery(query)) }
}

export function clearPrintFilterSession() {
  memory = null
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch (_) {
    // Storage may be disabled in the browser.
  }
}
